import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Divider,
  FormControlLabel, Switch, Radio, RadioGroup, FormControl, FormLabel,
  useMediaQuery, useTheme
} from '@mui/material';
import { Settings } from '@mui/icons-material';

export default function HoursSettingsDialog({
  open, onCancel, onSave,
  tempSettings, setTempSettings,
  variant = 'full' // 'full' = admin/manager, 'employee' = slimmed for employee view
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const toggle = (key) => (e) =>
    setTempSettings({ ...tempSettings, [key]: e.target.checked });

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Settings sx={{ mr: 1 }} />
        {variant === 'employee' ? 'Time Entry Settings' : 'Employee Hours Settings'}
      </DialogTitle>
      <DialogContent dividers>
        {/* Summary Cards Section */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
          Summary Cards
        </Typography>
        <Box sx={{ ml: 1, mb: 2, display: 'flex', flexDirection: 'column' }}>
          {variant !== 'employee' && (
            <FormControlLabel control={<Switch checked={tempSettings.showTotalEntries} onChange={toggle('showTotalEntries')} size="small" />} label="Total Entries" />
          )}
          <FormControlLabel control={<Switch checked={tempSettings.showTotalHours} onChange={toggle('showTotalHours')} size="small" />} label="Total Hours" />
          {variant !== 'employee' && (
            <FormControlLabel control={<Switch checked={tempSettings.showRegularHours} onChange={toggle('showRegularHours')} size="small" />} label="Regular Hours (Single Employee)" />
          )}
          <FormControlLabel control={<Switch checked={tempSettings.showOvertimeHours} onChange={toggle('showOvertimeHours')} size="small" />} label="Overtime Hours" />
          <FormControlLabel control={<Switch checked={tempSettings.showTotalPay} onChange={toggle('showTotalPay')} size="small" />} label="Total Pay" />
          {variant !== 'employee' && (
            <FormControlLabel control={<Switch checked={tempSettings.showAvgRate} onChange={toggle('showAvgRate')} size="small" />} label="Average Rate (All Employees)" />
          )}
          {variant !== 'employee' && (
            <FormControlLabel control={<Switch checked={tempSettings.showAvgPerEntry} onChange={toggle('showAvgPerEntry')} size="small" />} label="Avg Hours/Entry (Single Employee)" />
          )}
          <FormControlLabel control={<Switch checked={tempSettings.showTotalUnpaid} onChange={toggle('showTotalUnpaid')} size="small" />} label="Total Unpaid" />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Sections */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
          Sections
        </Typography>
        <Box sx={{ ml: 1, mb: 2, display: 'flex', flexDirection: 'column' }}>
          <FormControlLabel control={<Switch checked={tempSettings.showWeeklyBreakdown} onChange={toggle('showWeeklyBreakdown')} size="small" />} label="Weekly Breakdown" />
          {variant !== 'employee' && (
            <FormControlLabel control={<Switch checked={tempSettings.showDailyBreakdown} onChange={toggle('showDailyBreakdown')} size="small" />} label="Daily Breakdown" />
          )}
        </Box>

        {variant !== 'employee' && (
          <>
            <Divider sx={{ my: 2 }} />

            {/* Default Expanded */}
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: 'text.secondary' }}>
                Default Expanded Section
              </FormLabel>
              <RadioGroup
                value={tempSettings.defaultExpanded}
                onChange={(e) => setTempSettings({ ...tempSettings, defaultExpanded: e.target.value })}
              >
                <FormControlLabel value="entries" control={<Radio size="small" />} label="Time Entries" />
                <FormControlLabel value="weekly" control={<Radio size="small" />} label="Weekly Breakdown" />
                <FormControlLabel value="daily" control={<Radio size="small" />} label="Daily Breakdown" />
              </RadioGroup>
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={onSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
